import type { RadioTransport } from './web-serial.ts'

export const CHANNEL_RECORD_SIZE = 32
export const BLOCK_SIZE = 0x40
export const ACK = 0x06

export const CTCSS_TONES = [
  67.0, 69.3, 71.9, 74.4, 77.0, 79.7, 82.5, 85.4, 88.5, 91.5, 94.8, 97.4, 100.0,
  103.5, 107.2, 110.9, 114.8, 118.8, 123.0, 127.3, 131.8, 136.5, 141.3, 146.2,
  151.4, 156.7, 159.8, 162.2, 165.5, 167.9, 171.3, 173.8, 177.3, 179.9, 183.5,
  186.2, 189.9, 192.8, 196.6, 199.5, 203.5, 206.5, 210.7, 218.1, 225.7, 229.1,
  233.6, 241.8, 250.3, 254.1,
] as const

export const DTCS_CODES = [
  23, 25, 26, 31, 32, 36, 43, 47, 51, 53, 54, 65, 71, 72, 73, 74, 114, 115,
  116, 122, 125, 131, 132, 134, 143, 145, 152, 155, 156, 162, 165, 172, 174,
  205, 212, 223, 225, 226, 243, 244, 245, 246, 251, 252, 255, 261, 263, 265,
  266, 271, 274, 306, 311, 315, 325, 331, 332, 343, 346, 351, 356, 364, 365,
  371, 411, 412, 413, 423, 431, 432, 445, 446, 452, 454, 455, 462, 464, 465,
  466, 503, 506, 516, 523, 526, 532, 546, 565, 606, 612, 624, 627, 631, 632,
  645, 654, 662, 664, 703, 712, 723, 731, 732, 734, 743, 754,
] as const

const ENCRYPTION_KEYS = [
  'BHT ',
  'CO 7',
  'A ES',
  ' EIY',
  'M PQ',
  'XN Y',
  'RVB ',
  ' HQP',
  'W RC',
  'MS N',
  ' SAT',
  'K DH',
  'ZO R',
  'C SL',
  '6RB ',
  ' JCG',
  'PN V',
  'J PK',
  'EK L',
  'I LZ',
] as const

const UV17_SEND = '53454e4421050d010101041108050d0d01110f091209100400'
const BF_F8HP_PRO_SEND =
  '53454e44120d0a0a10030d02110c120a1106040e02090d0000'
const UV5RM_PLUS_SEND =
  '53454e44130f0d130e0e0e0d0a0e0e020f0804110c000d0e00'

type MemoryRange = readonly [start: number, size: number]

export type WebRadioProfile = {
  protocol: 'uv17'
  id: string
  vendor: string
  model: string
  summary: string
  tags: readonly string[]
  baudRate: 115200
  memorySize: number
  ranges: readonly MemoryRange[]
  channelCount: number
  modelResponseLength: number
  idents: readonly string[]
  sendPacketHex: string | null
  encryptionIndex: number | null
  powerLevels: readonly string[]
  nameLength: number
}

const UV5R_MINI_RANGES = [
  [0x0000, 0x8040],
  [0x9000, 0x0040],
  [0xA000, 0x01C0],
] as const

const UV17_RANGES = [
  [0x0000, 0x8040],
  [0x9000, 0x0040],
  [0xA000, 0x02C0],
  [0xD000, 0x0040],
] as const

const F8HP_RANGES = [
  [0x0000, 0x8040],
  [0x9000, 0x0080],
  [0xA000, 0x02C0],
  [0xD000, 0x00C0],
] as const

export const WEB_RADIO_PROFILES: readonly WebRadioProfile[] = [
  {
    protocol: 'uv17',
    id: 'baofeng-uv-5r-mini',
    vendor: 'Baofeng',
    model: 'UV-5R Mini',
    summary: 'USB programming for the popular compact UV-5R Mini.',
    tags: ['USB', 'Read + write', '999 channels'],
    baudRate: 115200,
    memorySize: 0x8240,
    ranges: UV5R_MINI_RANGES,
    channelCount: 999,
    modelResponseLength: 15,
    idents: ['PROGRAMCOLORPROU'],
    sendPacketHex: UV17_SEND,
    encryptionIndex: 1,
    powerLevels: ['High', 'Low'],
    nameLength: 12,
  },
  {
    protocol: 'uv17',
    id: 'baofeng-uv-5g-mini',
    vendor: 'Baofeng',
    model: 'UV-5G Mini',
    summary: 'UV-5 Mini family support, including both known firmware handshakes.',
    tags: ['USB', 'Read + write', 'GMRS'],
    baudRate: 115200,
    memorySize: 0x8240,
    ranges: UV5R_MINI_RANGES,
    channelCount: 999,
    modelResponseLength: 15,
    idents: ['PROGRAMGMRS5RMIU', 'PROGRAMCOLORPROU'],
    sendPacketHex: UV17_SEND,
    encryptionIndex: 1,
    powerLevels: ['High', 'Low'],
    nameLength: 12,
  },
  {
    protocol: 'uv17',
    id: 'baofeng-uv-32',
    vendor: 'Baofeng',
    model: 'UV-32',
    summary: 'UV-32 color-screen family with one thousand channel records.',
    tags: ['USB', 'Read + write', '1,000 channels'],
    baudRate: 115200,
    memorySize: 0x8380,
    ranges: UV17_RANGES,
    channelCount: 1000,
    modelResponseLength: 7,
    idents: ['PROGRAMCOLORPROU'],
    sendPacketHex: UV17_SEND,
    encryptionIndex: 1,
    powerLevels: ['High', 'Low', 'Medium'],
    nameLength: 12,
  },
  {
    protocol: 'uv17',
    id: 'baofeng-gm-32',
    vendor: 'Baofeng',
    model: 'GM-32',
    summary: 'Experimental NewChirp profile using the UV-32 memory family.',
    tags: ['USB', 'Experimental', 'GMRS'],
    baudRate: 115200,
    memorySize: 0x8380,
    ranges: UV17_RANGES,
    channelCount: 1000,
    modelResponseLength: 7,
    idents: ['PROGRAMCOLORPROU'],
    sendPacketHex: UV17_SEND,
    encryptionIndex: 1,
    powerLevels: ['High', 'Low', 'Medium'],
    nameLength: 12,
  },
  {
    protocol: 'uv17',
    id: 'baofeng-gm-21',
    vendor: 'Baofeng',
    model: 'GM-21',
    summary: 'Compact GMRS radio in the UV-17Pro GPS memory family.',
    tags: ['USB', 'Read + write', 'GMRS'],
    baudRate: 115200,
    memorySize: 0x8380,
    ranges: UV17_RANGES,
    channelCount: 1000,
    modelResponseLength: 7,
    idents: ['PROGRAMCOLORPROU'],
    sendPacketHex: UV17_SEND,
    encryptionIndex: 1,
    powerLevels: ['High', 'Low'],
    nameLength: 12,
  },
  {
    protocol: 'uv17',
    id: 'baofeng-uv-21pro-gps',
    vendor: 'Baofeng',
    model: 'UV-21ProGPS',
    summary: 'Popular tri-band handheld with color display and GPS.',
    tags: ['USB', 'Read + write', 'Tri-band'],
    baudRate: 115200,
    memorySize: 0x8380,
    ranges: UV17_RANGES,
    channelCount: 1000,
    modelResponseLength: 7,
    idents: ['PROGRAMCOLORPROU'],
    sendPacketHex: UV17_SEND,
    encryptionIndex: 1,
    powerLevels: ['High', 'Low'],
    nameLength: 12,
  },
  {
    protocol: 'uv17',
    id: 'baofeng-bf-f8hp-pro',
    vendor: 'Baofeng',
    model: 'BF-F8HP-PRO',
    summary: 'Newer high-power F8HP family with extended memory ranges.',
    tags: ['USB', 'Read + write', '1,000 channels'],
    baudRate: 115200,
    memorySize: 0x8440,
    ranges: F8HP_RANGES,
    channelCount: 1000,
    modelResponseLength: 6,
    idents: ['PROGRAMBF5RTECHU'],
    sendPacketHex: BF_F8HP_PRO_SEND,
    encryptionIndex: 3,
    powerLevels: ['High', 'Low', 'Mid'],
    nameLength: 12,
  },
  {
    protocol: 'uv17',
    id: 'baofeng-uv-5rm-plus',
    vendor: 'Baofeng',
    model: 'UV-5RM Plus',
    summary: 'UV-5RM Plus with the extended UV-17Pro channel map.',
    tags: ['USB', 'Read + write', '1,000 channels'],
    baudRate: 115200,
    memorySize: 0x8380,
    ranges: UV17_RANGES,
    channelCount: 1000,
    modelResponseLength: 15,
    idents: ['PROGRAMBFNORMALU'],
    sendPacketHex: UV5RM_PLUS_SEND,
    encryptionIndex: 13,
    powerLevels: ['High', 'Low', 'Medium'],
    nameLength: 12,
  },
  {
    protocol: 'uv17',
    id: 'baofeng-gm-5rh',
    vendor: 'Baofeng',
    model: 'GM-5RH',
    summary: 'GMRS handheld in the UV-17Pro protocol family.',
    tags: ['USB', 'Read + write', 'GMRS'],
    baudRate: 115200,
    memorySize: 0x8380,
    ranges: UV17_RANGES,
    channelCount: 1000,
    modelResponseLength: 15,
    idents: ['PROGRAMBFGMRS05U'],
    sendPacketHex: UV17_SEND,
    encryptionIndex: 1,
    powerLevels: ['High', 'Low', 'Medium'],
    nameLength: 12,
  },
  {
    protocol: 'uv17',
    id: 'baofeng-uv-17pro',
    vendor: 'Baofeng',
    model: 'UV-17Pro',
    summary: 'Popular UV-17Pro analog handheld with one thousand channel slots.',
    tags: ['USB', 'Read + write', '1,000 channels'],
    baudRate: 115200,
    memorySize: 0x8380,
    ranges: UV17_RANGES,
    channelCount: 1000,
    modelResponseLength: 15,
    idents: ['PROGRAMBFNORMALU'],
    sendPacketHex: UV17_SEND,
    encryptionIndex: 1,
    powerLevels: ['High', 'Low'],
    nameLength: 12,
  },
  {
    protocol: 'uv17',
    id: 'baofeng-uv-21pro-v2',
    vendor: 'Baofeng',
    model: 'UV-21ProV2',
    summary: 'UV-21Pro V2 analog and airband receive support.',
    tags: ['USB', 'Read + write', 'Tri-band'],
    baudRate: 115200,
    memorySize: 0x8380,
    ranges: UV17_RANGES,
    channelCount: 1000,
    modelResponseLength: 15,
    idents: ['PROGRAMBFNORMALU'],
    sendPacketHex: UV17_SEND,
    encryptionIndex: 1,
    powerLevels: ['High', 'Low'],
    nameLength: 12,
  },
  {
    protocol: 'uv17',
    id: 'baofeng-uv-25',
    vendor: 'Baofeng',
    model: 'UV-25',
    summary: 'UV-25 in the UV-17Pro programming family.',
    tags: ['USB', 'Read + write', '1,000 channels'],
    baudRate: 115200,
    memorySize: 0x8380,
    ranges: UV17_RANGES,
    channelCount: 1000,
    modelResponseLength: 15,
    idents: ['PROGRAMBFNORMALU'],
    sendPacketHex: UV17_SEND,
    encryptionIndex: 1,
    powerLevels: ['High', 'Low'],
    nameLength: 12,
  },
  {
    protocol: 'uv17',
    id: 'baofeng-uv-17pro-gps',
    vendor: 'Baofeng',
    model: 'UV-17ProGPS',
    summary: 'GPS-equipped UV-17Pro family with color display support.',
    tags: ['USB', 'Read + write', 'GPS'],
    baudRate: 115200,
    memorySize: 0x8380,
    ranges: UV17_RANGES,
    channelCount: 1000,
    modelResponseLength: 7,
    idents: ['PROGRAMCOLORPROU'],
    sendPacketHex: UV17_SEND,
    encryptionIndex: 1,
    powerLevels: ['High', 'Low'],
    nameLength: 12,
  },
  {
    protocol: 'uv17',
    id: 'baofeng-uv-28plus',
    vendor: 'Baofeng',
    model: 'UV-28Plus',
    summary: 'UV-28Plus color-screen and Bluetooth-capable family.',
    tags: ['USB', 'Read + write', 'Tri-power'],
    baudRate: 115200,
    memorySize: 0x8380,
    ranges: UV17_RANGES,
    channelCount: 1000,
    modelResponseLength: 7,
    idents: ['PROGRAMCOLORPROU'],
    sendPacketHex: UV17_SEND,
    encryptionIndex: 1,
    powerLevels: ['High', 'Low', 'Medium'],
    nameLength: 12,
  },
  {
    protocol: 'uv17',
    id: 'radioddity-gm-30-plus',
    vendor: 'Radioddity',
    model: 'GM-30 Plus',
    summary: 'GMRS radio sharing the UV-17Pro GPS protocol family.',
    tags: ['USB', 'Read + write', 'GMRS'],
    baudRate: 115200,
    memorySize: 0x8380,
    ranges: UV17_RANGES,
    channelCount: 1000,
    modelResponseLength: 7,
    idents: ['PROGRAMCOLORPROU'],
    sendPacketHex: UV17_SEND,
    encryptionIndex: 1,
    powerLevels: ['High', 'Low'],
    nameLength: 12,
  },
  {
    protocol: 'uv17',
    id: 'radioddity-gm-30-pro',
    vendor: 'Radioddity',
    model: 'GM-30 Pro',
    summary: 'GMRS color-screen model with Bluetooth and scramble fields.',
    tags: ['USB', 'Read + write', 'GMRS'],
    baudRate: 115200,
    memorySize: 0x8380,
    ranges: UV17_RANGES,
    channelCount: 1000,
    modelResponseLength: 7,
    idents: ['PROGRAMCOLORPROU'],
    sendPacketHex: UV17_SEND,
    encryptionIndex: 1,
    powerLevels: ['High', 'Low'],
    nameLength: 12,
  },
  {
    protocol: 'uv17',
    id: 'baofeng-5rm',
    vendor: 'Baofeng',
    model: '5RM',
    summary: 'Baofeng 5RM with the UV-17Pro memory layout.',
    tags: ['USB', 'Read + write', 'Tri-power'],
    baudRate: 115200,
    memorySize: 0x8380,
    ranges: UV17_RANGES,
    channelCount: 1000,
    modelResponseLength: 15,
    idents: ['PROGRAMBFNORMALU'],
    sendPacketHex: UV17_SEND,
    encryptionIndex: 1,
    powerLevels: ['High', 'Low', 'Medium'],
    nameLength: 12,
  },
  {
    protocol: 'uv17',
    id: 'baofeng-k5-plus',
    vendor: 'Baofeng',
    model: 'K5-Plus',
    summary: 'K5-Plus radio in the 5RM programming family.',
    tags: ['USB', 'Read + write', 'Tri-power'],
    baudRate: 115200,
    memorySize: 0x8380,
    ranges: UV17_RANGES,
    channelCount: 1000,
    modelResponseLength: 15,
    idents: ['PROGRAMBFNORMALU'],
    sendPacketHex: UV17_SEND,
    encryptionIndex: 1,
    powerLevels: ['High', 'Low', 'Medium'],
    nameLength: 12,
  },
  {
    protocol: 'uv17',
    id: 'maxtalker-p15',
    vendor: 'MaxTalker',
    model: 'P15',
    summary: 'MaxTalker P15 using the Baofeng 5RM protocol.',
    tags: ['USB', 'Read + write', 'Tri-power'],
    baudRate: 115200,
    memorySize: 0x8380,
    ranges: UV17_RANGES,
    channelCount: 1000,
    modelResponseLength: 15,
    idents: ['PROGRAMBFNORMALU'],
    sendPacketHex: UV17_SEND,
    encryptionIndex: 1,
    powerLevels: ['High', 'Low', 'Medium'],
    nameLength: 12,
  },
  {
    protocol: 'uv17',
    id: 'radioddity-uv-5g-plus',
    vendor: 'Radioddity',
    model: 'UV-5G Plus',
    summary: 'GMRS UV-5G Plus sharing the GM-5RH protocol.',
    tags: ['USB', 'Read + write', 'GMRS'],
    baudRate: 115200,
    memorySize: 0x8380,
    ranges: UV17_RANGES,
    channelCount: 1000,
    modelResponseLength: 15,
    idents: ['PROGRAMBFGMRS05U'],
    sendPacketHex: UV17_SEND,
    encryptionIndex: 1,
    powerLevels: ['High', 'Low', 'Medium'],
    nameLength: 12,
  },
  {
    protocol: 'uv17',
    id: 'maxtalker-mt-5rm',
    vendor: 'MaxTalker',
    model: 'MT-5RM',
    summary: 'MaxTalker MT-5RM using the Baofeng 5RM programming family.',
    tags: ['USB', 'Read + write', 'Tri-power'],
    baudRate: 115200,
    memorySize: 0x8380,
    ranges: UV17_RANGES,
    channelCount: 1000,
    modelResponseLength: 15,
    idents: ['PROGRAMBFNORMALU'],
    sendPacketHex: UV17_SEND,
    encryptionIndex: 1,
    powerLevels: ['High', 'Low', 'Medium'],
    nameLength: 12,
  },
  {
    protocol: 'uv17',
    id: 'baofeng-k6',
    vendor: 'Baofeng',
    model: 'K6',
    summary: 'K6 extended-UHF radio with an unencrypted UV-17 clone map.',
    tags: ['USB', 'Read + write', '999 channels', 'Extended UHF'],
    baudRate: 115200,
    memorySize: 0x8400,
    ranges: [
      [0x0000, 0x8040],
      [0x8040, 0x0080],
      [0x80c0, 0x02c0],
      [0x8380, 0x0040],
      [0x83c0, 0x0040],
    ],
    channelCount: 999,
    modelResponseLength: 0,
    idents: ['PROGRAMBF-K6PROU'],
    sendPacketHex: null,
    encryptionIndex: null,
    powerLevels: ['High', 'Low'],
    nameLength: 12,
  },
  {
    protocol: 'uv17',
    id: 'baofeng-uv-17r-plus',
    vendor: 'Baofeng',
    model: 'UV-17R-Plus',
    summary: 'UV-17R Plus analog and airband receive support.',
    tags: ['USB', 'Read + write', '1,000 channels'],
    baudRate: 115200,
    memorySize: 0x8380,
    ranges: UV17_RANGES,
    channelCount: 1000,
    modelResponseLength: 15,
    idents: ['PROGRAMBFNORMALU'],
    sendPacketHex: UV17_SEND,
    encryptionIndex: 1,
    powerLevels: ['High', 'Low'],
    nameLength: 12,
  },
  {
    protocol: 'uv17',
    id: 'baofeng-uv-5rh',
    vendor: 'Baofeng',
    model: 'UV-5RH',
    summary: 'UV-5RH color-screen radio with tri-power selection.',
    tags: ['USB', 'Read + write', 'Tri-power'],
    baudRate: 115200,
    memorySize: 0x8380,
    ranges: UV17_RANGES,
    channelCount: 1000,
    modelResponseLength: 15,
    idents: ['PROGRAMBFNORMALU'],
    sendPacketHex: UV17_SEND,
    encryptionIndex: 1,
    powerLevels: ['High', 'Low', 'Medium'],
    nameLength: 12,
  },
] as const

export type RadioChannelExtra = {
  name: string
  label: string
  kind: 'boolean' | 'choice' | 'integer' | 'number' | 'string'
  value: string | number | boolean
  choices?: readonly string[]
  minimum?: number
  maximum?: number
  documentation?: string
  mutable?: boolean
  visibleModes?: readonly string[]
}

export type WebChannel = {
  number: number
  empty: boolean
  name: string
  rxFrequency: number | null
  txFrequency: number | null
  toneMode: 'none' | 'ctcss' | 'dtcs'
  ctcss: number | null
  dtcs: number | null
  dtcsPolarity: 'N' | 'R'
  mode: string
  power: string
  skip: boolean
  tuningStep?: number
  rawData?: string
  extras?: RadioChannelExtra[]
}

export function profileById(id: string): WebRadioProfile {
  const profile = WEB_RADIO_PROFILES.find((candidate) => candidate.id === id)
  if (!profile) throw new Error(`Unknown web radio profile: ${id}`)
  return profile
}

export function decodeLbcd(raw: Uint8Array): number | null {
  if (raw.every((value) => value === 0xff)) return null
  let value = 0
  for (let index = raw.length - 1; index >= 0; index -= 1) {
    const high = raw[index] >> 4
    const low = raw[index] & 0x0f
    if (high > 9 || low > 9) return null
    value = value * 100 + high * 10 + low
  }
  return value * 10
}

export function encodeLbcd(frequency: number, target?: Uint8Array): Uint8Array {
  const result = target ?? new Uint8Array(4)
  let value = Math.round(frequency / 10)
  for (let index = 0; index < result.length; index += 1) {
    const part = value % 100
    result[index] = ((Math.floor(part / 10) << 4) | (part % 10)) & 0xff
    value = Math.floor(value / 100)
  }
  return result
}

function decodeName(raw: Uint8Array, length: number): string {
  let end = raw.indexOf(0xff)
  if (end < 0) end = raw.indexOf(0)
  if (end < 0) end = length
  if (end > length) end = length
  if (end === 0) return ''
  const bytes = raw.slice(0, end)
  try {
    return new TextDecoder('gb18030').decode(bytes).trim()
  } catch {
    return String.fromCharCode(...bytes).trim()
  }
}

function encodeName(name: string, length: number): Uint8Array {
  const result = new Uint8Array(length).fill(0xff)
  const text = name.trim().slice(0, length)
  for (let index = 0; index < text.length; index += 1) {
    const code = text.charCodeAt(index)
    result[index] = code < 0x80 ? code : 0x3f
  }
  return result
}

function decodeTone(raw: Uint8Array): Pick<
  WebChannel,
  'toneMode' | 'ctcss' | 'dtcs' | 'dtcsPolarity'
> {
  const value = raw[0] | (raw[1] << 8)
  if (value === 0 || value === 0xffff) {
    return { toneMode: 'none', ctcss: null, dtcs: null, dtcsPolarity: 'N' }
  }
  if (value >= 0x0258) {
    return {
      toneMode: 'ctcss',
      ctcss: value / 10,
      dtcs: null,
      dtcsPolarity: 'N',
    }
  }
  const reversed = value > 0x69
  const index = reversed ? value - 0x6a : value - 1
  return {
    toneMode: 'dtcs',
    ctcss: null,
    dtcs: DTCS_CODES[index] ?? null,
    dtcsPolarity: reversed ? 'R' : 'N',
  }
}

function encodeTone(
  target: Uint8Array,
  mode: WebChannel['toneMode'],
  ctcss: number | null,
  dtcs: number | null,
  polarity: 'N' | 'R',
): void {
  let value = 0
  if (mode === 'ctcss' && ctcss) {
    value = Math.round(ctcss * 10)
  } else if (mode === 'dtcs' && dtcs) {
    const index = DTCS_CODES.indexOf(dtcs as (typeof DTCS_CODES)[number])
    if (index >= 0) value = index + 1 + (polarity === 'R' ? 0x69 : 0)
  }
  target[0] = value & 0xff
  target[1] = (value >> 8) & 0xff
}

export function parseChannel(
  image: Uint8Array,
  profile: WebRadioProfile,
  number: number,
): WebChannel {
  const offset = (number - 1) * CHANNEL_RECORD_SIZE
  const raw = image.slice(offset, offset + CHANNEL_RECORD_SIZE)
  if (raw.byteLength < CHANNEL_RECORD_SIZE || raw[0] === 0xff) {
    return {
      number,
      empty: true,
      name: '',
      rxFrequency: null,
      txFrequency: null,
      toneMode: 'none',
      ctcss: null,
      dtcs: null,
      dtcsPolarity: 'N',
      mode: 'FM',
      power: profile.powerLevels[0] ?? 'High',
      skip: false,
    }
  }

  const rx = decodeLbcd(raw.slice(0, 4))
  const tx = decodeLbcd(raw.slice(4, 8))
  const tone = decodeTone(raw.slice(8, 10))
  const powerIndex = raw[14] & 0x03
  return {
    number,
    empty: false,
    name: decodeName(raw.slice(20, 20 + profile.nameLength), profile.nameLength),
    rxFrequency: rx,
    txFrequency: tx,
    ...tone,
    mode: (raw[15] & 0x40) !== 0 ? 'NFM' : 'FM',
    power: profile.powerLevels[powerIndex] ?? profile.powerLevels[0] ?? 'High',
    skip: (raw[15] & 0x04) === 0,
  }
}

export function parseChannels(
  image: Uint8Array,
  profile: WebRadioProfile,
): WebChannel[] {
  return Array.from(
    { length: profile.channelCount },
    (_, index) => parseChannel(image, profile, index + 1),
  )
}

export function applyChannel(
  image: Uint8Array,
  profile: WebRadioProfile,
  channel: WebChannel,
): void {
  const offset = (channel.number - 1) * CHANNEL_RECORD_SIZE
  const raw = image.subarray(offset, offset + CHANNEL_RECORD_SIZE)
  if (channel.empty) {
    raw.fill(0xff)
    return
  }
  if (!channel.rxFrequency) throw new Error('A receive frequency is required.')

  const wasEmpty = raw[0] === 0xff
  const preservedPowerFlags = wasEmpty ? 0 : raw[14] & 0xfc
  const preservedChannelFlags = wasEmpty ? 0 : raw[15] & 0xbb
  if (wasEmpty) raw.fill(0xff)
  encodeLbcd(channel.rxFrequency, raw.subarray(0, 4))
  if (channel.txFrequency) {
    encodeLbcd(channel.txFrequency, raw.subarray(4, 8))
  } else {
    raw.subarray(4, 8).fill(0xff)
  }

  encodeTone(
    raw.subarray(8, 10),
    channel.toneMode,
    channel.ctcss,
    channel.dtcs,
    channel.dtcsPolarity,
  )
  const txTone = new Uint8Array(2)
  encodeTone(
    txTone,
    channel.toneMode,
    channel.ctcss,
    channel.dtcs,
    channel.dtcsPolarity,
  )
  raw.set(txTone, 10)

  const powerIndex = Math.max(0, profile.powerLevels.indexOf(channel.power))
  raw[14] = preservedPowerFlags | (powerIndex & 0x03)
  raw[15] =
    preservedChannelFlags |
    (channel.mode === 'NFM' ? 0x40 : 0) |
    (channel.skip ? 0 : 0x04)
  raw.set(encodeName(channel.name, profile.nameLength), 20)
}

export function encryptRadioBytes(data: Uint8Array, symbolIndex: number): Uint8Array {
  const key = new TextEncoder().encode(ENCRYPTION_KEYS[symbolIndex])
  const result = data.slice()
  for (let index = 0; index < result.length; index += 1) {
    const value = result[index]
    const symbol = key[index % key.length]
    if (
      symbol !== 0x20 &&
      value !== 0 &&
      value !== 0xff &&
      value !== symbol &&
      value !== (symbol ^ 0xff)
    ) {
      result[index] = value ^ symbol
    }
  }
  return result
}

function makeFrame(command: number, address: number, length: number): Uint8Array {
  return Uint8Array.from([
    command,
    (address >> 8) & 0xff,
    address & 0xff,
    length & 0xff,
  ])
}

function fromHex(value: string): Uint8Array {
  return Uint8Array.from({ length: value.length / 2 }, (_, index) =>
    Number.parseInt(value.slice(index * 2, index * 2 + 2), 16),
  )
}

async function handshake(
  profile: WebRadioProfile,
  transport: RadioTransport,
): Promise<string> {
  let identified = false
  for (const ident of profile.idents) {
    await transport.discardInput()
    await transport.write(new TextEncoder().encode(ident))
    try {
      const reply = await transport.readExact(1, 1500)
      if (reply[0] === ACK) {
        identified = true
        break
      }
    } catch {
      // Try the next known firmware handshake.
    }
  }
  if (!identified) {
    throw new Error(
      `The ${profile.vendor} ${profile.model} radio did not acknowledge programming mode. Confirm this is the exact model selected, the radio is powered on, and the cable is connected.`,
    )
  }

  await transport.write(Uint8Array.of(0x46))
  await transport.readExact(16, 2000)
  let model = ''
  if (profile.modelResponseLength > 0) {
    await transport.write(Uint8Array.of(0x4d))
    const modelReply = await transport.readExact(
      profile.modelResponseLength,
      2000,
    )
    model = new TextDecoder('ascii')
      .decode(modelReply)
      .replace(/[\x00-\x1f\x7f-\xff]/g, '')
      .trim()
  }

  if (profile.sendPacketHex) {
    await transport.write(fromHex(profile.sendPacketHex))
    const sendAck = await transport.readExact(1, 2000)
    if (sendAck[0] !== ACK) {
      throw new Error('The radio rejected the programming-session handshake.')
    }
  }
  return model || profile.model
}

export type RadioProgress = {
  current: number
  total: number
  label: string
}

function totalBlocks(profile: WebRadioProfile): number {
  return profile.ranges.reduce(
    (total, [, size]) => total + Math.ceil(size / BLOCK_SIZE),
    0,
  )
}

export async function readRadioWithTransport(
  profile: WebRadioProfile,
  transport: RadioTransport,
  onProgress?: (progress: RadioProgress) => void,
): Promise<{ image: Uint8Array; reportedModel: string }> {
  const reportedModel = await handshake(profile, transport)
  const image = new Uint8Array(profile.memorySize).fill(0xff)
  const total = totalBlocks(profile)
  let completed = 0
  let imageOffset = 0

  for (const [start, size] of profile.ranges) {
    for (let address = start; address < start + size; address += BLOCK_SIZE) {
      await transport.write(makeFrame(0x52, address, BLOCK_SIZE))
      const reply = await transport.readExact(BLOCK_SIZE + 4, 3500)
      const payload =
        profile.encryptionIndex === null
          ? reply.slice(4)
          : encryptRadioBytes(reply.slice(4), profile.encryptionIndex)
      image.set(payload, imageOffset)
      imageOffset += payload.byteLength
      completed += 1
      onProgress?.({ current: completed, total, label: 'Reading radio memory' })
    }
  }
  return { image, reportedModel }
}

export async function writeRadioWithTransport(
  profile: WebRadioProfile,
  image: Uint8Array,
  transport: RadioTransport,
  onProgress?: (progress: RadioProgress) => void,
): Promise<void> {
  if (image.byteLength < profile.memorySize) {
    throw new Error('The selected image is smaller than this radio’s memory map.')
  }
  await handshake(profile, transport)
  const total = totalBlocks(profile)
  let completed = 0
  let imageOffset = 0

  for (const [start, size] of profile.ranges) {
    for (let address = start; address < start + size; address += BLOCK_SIZE) {
      const payload = image.slice(imageOffset, imageOffset + BLOCK_SIZE)
      if (payload.byteLength !== BLOCK_SIZE) {
        throw new Error(`Incomplete memory block at image offset 0x${imageOffset.toString(16)}.`)
      }
      const encrypted =
        profile.encryptionIndex === null
          ? payload
          : encryptRadioBytes(payload, profile.encryptionIndex)
      const frame = new Uint8Array(4 + BLOCK_SIZE)
      frame.set(makeFrame(0x57, address, BLOCK_SIZE))
      frame.set(encrypted, 4)
      await transport.write(frame)
      const ack = await transport.readExact(1, 2500)
      if (ack[0] !== ACK) {
        throw new Error(`The radio rejected block 0x${address.toString(16)}.`)
      }
      imageOffset += BLOCK_SIZE
      completed += 1
      onProgress?.({ current: completed, total, label: 'Writing radio memory' })
    }
  }
}

export function compareImages(left: Uint8Array, right: Uint8Array): number | null {
  if (left.byteLength !== right.byteLength) return 0
  for (let index = 0; index < left.byteLength; index += 1) {
    if (left[index] !== right[index]) return index
  }
  return null
}

const CHIRP_IMAGE_MARKER = Uint8Array.from([
  0x00, 0xff, 0x63, 0x68, 0x69, 0x72, 0x70, 0xee, 0x69, 0x6d, 0x67, 0x00, 0x01,
])

export function createChirpImage(
  profile: WebRadioProfile,
  image: Uint8Array,
): Uint8Array {
  const metadata = {
    mem_extra: {},
    rclass: 'DynamicRadioAlias',
    vendor: profile.vendor,
    model: profile.model,
    variant: '',
    chirp_version: 'py3dev',
  }
  const encoded = new TextEncoder().encode(btoa(JSON.stringify(metadata)))
  const result = new Uint8Array(
    profile.memorySize + CHIRP_IMAGE_MARKER.byteLength + encoded.byteLength,
  )
  result.set(image.slice(0, profile.memorySize))
  result.set(CHIRP_IMAGE_MARKER, profile.memorySize)
  result.set(encoded, profile.memorySize + CHIRP_IMAGE_MARKER.byteLength)
  return result
}

export function extractImage(
  profile: WebRadioProfile,
  bytes: Uint8Array,
): Uint8Array {
  if (bytes.byteLength < profile.memorySize) {
    throw new Error(
      `This file is ${bytes.byteLength.toLocaleString()} bytes; ${profile.model} requires at least ${profile.memorySize.toLocaleString()}.`,
    )
  }
  return bytes.slice(0, profile.memorySize)
}
